import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { DateRangePicker, PRESETS } from "@/components/DateRangePicker";
import {
  CalendarDays, Users, Receipt, TrendingUp, Megaphone,
  AlertTriangle, FileText, Clock, Cake, UserX, BarChart3
} from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

function ActionCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <button onClick={onClick} className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-3.5 text-left hover:shadow-sm transition-shadow flex items-center gap-3" data-testid={`action-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "10" }}><Icon className="w-4 h-4" style={{ color }} /></div>
      <div><p className="text-lg font-medium text-[#1A1B1A]" style={H}>{value}</p><p className="text-[10px] text-[#757675]">{label}</p></div>
    </button>
  );
}

function StatRow({ label, value, sub }) {
  return <div className="flex items-center justify-between py-2 border-b border-[#1A1B1A]/[0.04] last:border-0"><div><p className="text-sm text-[#1A1B1A]">{label}</p>{sub && <p className="text-[10px] text-[#757675]">{sub}</p>}</div><p className="text-sm font-semibold text-[#1A1B1A]">{value}</p></div>;
}

export default function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "appointments");

  // Reporting period lives in the URL so it survives tab switches and refreshes
  const preset = params.get("preset") || "last30";
  const defaults = PRESETS[preset]?.range ? PRESETS[preset].range() : PRESETS.last30.range();
  const from = params.get("from") || defaults[0];
  const to = params.get("to") || defaults[1];

  const setRange = ({ preset: p, from: f, to: t }) => {
    const next = new URLSearchParams(params);
    next.set("preset", p);
    next.set("from", f);
    next.set("to", t);
    setParams(next, { replace: true });
  };

  const changeTab = (k) => {
    setTab(k);
    const next = new URLSearchParams(params);
    next.set("tab", k);
    next.set("preset", preset);
    next.set("from", from);
    next.set("to", to);
    setParams(next, { replace: true });
  };
  const [actions, setActions] = useState({});
  const [apptReport, setApptReport] = useState(null);
  const [patientReport, setPatientReport] = useState(null);
  const [txReport, setTxReport] = useState(null);
  const [pracReport, setPracReport] = useState(null);
  const [mktReport, setMktReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadReports = useCallback(() => {
    if (!from || !to) return;
    setLoading(true);
    const q = `?date_from=${from}&date_to=${to}`;
    Promise.all([
      api.get(`/reports/action-items${q}`), api.get(`/reports/appointments${q}`),
      api.get(`/reports/patients${q}`), api.get(`/reports/transactions${q}`),
      api.get(`/reports/practitioners${q}`), api.get(`/reports/marketing${q}`),
    ]).then(([a, ap, pa, tx, pr, mk]) => {
      setActions(a.data); setApptReport(ap.data); setPatientReport(pa.data);
      setTxReport(tx.data); setPracReport(pr.data); setMktReport(mk.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const TABS = [
    { k: "appointments", l: "Appointments", icon: CalendarDays },
    { k: "patients", l: "Patients", icon: Users },
    { k: "transactions", l: "Transactions", icon: Receipt },
    { k: "practitioners", l: "Practitioners", icon: BarChart3 },
    { k: "marketing", l: "Marketing", icon: Megaphone },
  ];

  if (loading) return <div className="p-6 md:p-8"><div className="animate-pulse space-y-4"><div className="h-8 bg-[#1A1B1A]/[0.04] rounded-xl w-48" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <div key={i} className="h-16 bg-[#1A1B1A]/[0.04] rounded-xl" />)}</div></div></div>;

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="reports-page">
      <h1 className="text-2xl font-medium text-[#1A1B1A] mb-5" style={H}>Reports</h1>

      <DateRangePicker preset={preset} from={from} to={to} onChange={setRange} />

      {/* Action items */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="action-items">
        <ActionCard icon={CalendarDays} label="Uninvoiced appointments" value={actions.uninvoiced_appointments || 0} color="#C4553A" />
        <ActionCard icon={FileText} label="Draft notes" value={actions.draft_notes || 0} color="#496275" />
        <ActionCard icon={Receipt} label="Outstanding invoices" value={actions.outstanding_invoices || 0} color="#D97706" />
        <ActionCard icon={Clock} label="Overdue recalls" value={actions.overdue_recalls || 0} color="#205B5A" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[#1A1B1A]/[0.06] overflow-x-auto">
        {TABS.map(t => (
          <button key={t.k} onClick={() => changeTab(t.k)} data-testid={`report-tab-${t.k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 transition-colors ${tab === t.k ? "text-[#205B5A] border-[#205B5A]" : "text-[#757675] border-transparent hover:text-[#1A1B1A]"}`}>
            <t.icon className="w-3.5 h-3.5" />{t.l}
          </button>
        ))}
      </div>

      {/* Appointments */}
      {tab === "appointments" && apptReport && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
            <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3" style={H}>Schedule summary</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center"><p className="text-2xl font-medium text-[#205B5A]" style={H}>{apptReport.total}</p><p className="text-[10px] text-[#757675]">Total</p></div>
              <div className="text-center"><p className="text-2xl font-medium text-[#205B5A]" style={H}>{apptReport.completed}</p><p className="text-[10px] text-[#757675]">Completed</p></div>
              <div className="text-center"><p className="text-2xl font-medium text-[#C4553A]" style={H}>{apptReport.missed}</p><p className="text-[10px] text-[#757675]">Missed</p></div>
            </div>
            <h4 className="text-[10px] font-semibold text-[#757675] uppercase mb-2">By status</h4>
            {Object.entries(apptReport.by_status || {}).map(([s, c]) => <StatRow key={s} label={s} value={c} />)}
          </div>
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
              <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3" style={H}>By type</h3>
              {Object.entries(apptReport.by_type || {}).map(([t, c]) => <StatRow key={t} label={t} value={c} />)}
            </div>
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
              <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3" style={H}>Note completion</h3>
              <StatRow label="Final notes" value={apptReport.notes?.final || 0} />
              <StatRow label="Draft notes" value={apptReport.notes?.draft || 0} />
            </div>
          </div>
        </div>
      )}

      {/* Patients */}
      {tab === "patients" && patientReport && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
            <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3 flex items-center gap-1.5" style={H}><Cake className="w-4 h-4 text-[#205B5A]" /> Upcoming birthdays</h3>
            {patientReport.upcoming_birthdays?.length === 0 ? <p className="text-xs text-[#757675]">No upcoming birthdays</p> : (
              patientReport.upcoming_birthdays.map(p => <StatRow key={p.id} label={`${p.first_name} ${p.last_name}`} value={`${p.days_until}d`} sub={`Birthday: ${p.birthday}`} />)
            )}
          </div>
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
              <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3 flex items-center gap-1.5" style={H}><UserX className="w-4 h-4 text-[#C4553A]" /> Without upcoming appointments</h3>
              {patientReport.without_appointments?.slice(0, 8).map(p => <StatRow key={p.id} label={`${p.first_name} ${p.last_name}`} value="No booking" />)}
              {patientReport.without_appointments?.length === 0 && <p className="text-xs text-[#757675]">All patients have upcoming appointments</p>}
            </div>
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
              <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3" style={H}>Recalls</h3>
              <StatRow label="Pending recalls" value={patientReport.pending_recalls || 0} />
              <StatRow label="Overdue recalls" value={patientReport.overdue_recalls || 0} />
            </div>
          </div>
        </div>
      )}

      {/* Transactions */}
      {tab === "transactions" && txReport && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
            <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3" style={H}>Revenue</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center"><p className="text-xl font-medium text-[#205B5A]" style={H}>£{txReport.total_revenue?.toFixed(0)}</p><p className="text-[10px] text-[#757675]">Payments</p></div>
              <div className="text-center"><p className="text-xl font-medium text-[#1A1B1A]" style={H}>£{txReport.total_invoiced?.toFixed(0)}</p><p className="text-[10px] text-[#757675]">Invoiced</p></div>
              <div className="text-center"><p className="text-xl font-medium text-[#C4553A]" style={H}>£{txReport.outstanding?.toFixed(0)}</p><p className="text-[10px] text-[#757675]">Outstanding</p></div>
            </div>
            <h4 className="text-[10px] font-semibold text-[#757675] uppercase mb-2">By payment method</h4>
            {Object.entries(txReport.by_method || {}).map(([m, v]) => <StatRow key={m} label={m} value={`£${v.toFixed(2)}`} />)}
          </div>
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
            <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3" style={H}>Daily payments (last 30 days)</h3>
            <div className="space-y-0.5 max-h-64 overflow-y-auto">
              {Object.entries(txReport.by_day || {}).sort((a, b) => b[0].localeCompare(a[0])).map(([d, v]) => <StatRow key={d} label={d} value={`£${v.toFixed(2)}`} />)}
              {Object.keys(txReport.by_day || {}).length === 0 && <p className="text-xs text-[#757675]">No payments in this period</p>}
            </div>
          </div>
        </div>
      )}

      {/* Practitioners */}
      {tab === "practitioners" && pracReport && (
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
          <table className="w-full"><thead><tr className="border-b border-[#1A1B1A]/[0.06]">
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Practitioner</th>
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Role</th>
            <th className="text-right px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Appointments</th>
            <th className="text-right px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Completed</th>
            <th className="text-right px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Revenue raised</th>
            <th className="text-right px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Revenue closed</th>
          </tr></thead><tbody>
            {pracReport.practitioners?.map(p => (
              <tr key={p.id} className="border-b border-[#1A1B1A]/[0.03]">
                <td className="px-5 py-3 text-sm font-medium text-[#1A1B1A]">{p.name}</td>
                <td className="px-5 py-3 text-sm text-[#757675] capitalize">{p.role}</td>
                <td className="px-5 py-3 text-sm text-[#1A1B1A] text-right">{p.total_appointments}</td>
                <td className="px-5 py-3 text-sm text-[#1A1B1A] text-right">{p.completed}</td>
                <td className="px-5 py-3 text-sm text-[#1A1B1A] text-right">£{p.revenue_raised?.toFixed(2)}</td>
                <td className="px-5 py-3 text-sm font-semibold text-[#205B5A] text-right">£{p.revenue_closed?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      {/* Marketing */}
      {tab === "marketing" && mktReport && (
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
          <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3" style={H}>Referral sources</h3>
          <p className="text-xs text-[#757675] mb-4">{mktReport.total_patients} total patients</p>
          {mktReport.by_referral_source?.map(([src, count]) => (
            <div key={src} className="flex items-center gap-3 py-2 border-b border-[#1A1B1A]/[0.04] last:border-0">
              <div className="flex-1"><p className="text-sm text-[#1A1B1A]">{src}</p></div>
              <div className="w-32 h-2 bg-[#FAF9F6] rounded-full overflow-hidden"><div className="h-full bg-[#205B5A] rounded-full" style={{ width: `${(count / mktReport.total_patients) * 100}%` }} /></div>
              <span className="text-sm font-medium text-[#1A1B1A] w-8 text-right">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
